// Contacts Module Tour
// Deep dive into contact management features

export const contactsTour = {
  id: 'contacts',
  name: 'Contacts Module Tour',
  displayName: 'Master Your Contact Database',
  description: 'Learn how to manage, organize, and communicate with your contacts effectively',
  estimatedDuration: '5 minutes',
  category: 'feature',
  autoStart: false, // Prompt user when they first visit Contacts
  prerequisiteTours: ['ftux'], // Should complete FTUX first
  
  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    showProgress: true
  },
  
  steps: [
    // Step 1: Contacts Overview
    {
      element: 'nav #sb_contacts',
      popover: {
        title: '📋 Welcome to Contacts',
        description: `
          This is your contact database—the heart of your business. Every person 
          you interact with, from cold leads to loyal clients, is managed here. 
          Let's explore how to make the most of this powerful tool.
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 2: Smart Lists Sidebar
    {
      element: '#tb_lists',
      popover: {
        title: '📂 Smart Lists: Auto-Segmentation',
        description: `
          Smart Lists automatically group contacts based on criteria you set. 
          "All" shows everyone. You can create lists like "Hot Leads", 
          "VIP Clients", or "Inactive Contacts" that update automatically 
          as contact details change.
        `,
        side: 'right',
        align: 'start',
        proTip: 'Create Smart Lists based on tags, custom fields, or behavior for laser-focused targeting.'
      }
    },

    // Step 3: Search Bar
    {
      element: 'input[type="search"], input[placeholder*="Search"]',
      popover: {
        title: '🔍 Lightning-Fast Search',
        description: `
          Find any contact instantly by name, email, phone, or even notes. 
          The search is smart—it looks through all contact fields and returns 
          results as you type. Try searching for a partial name or phone number.
        `,
        side: 'bottom',
        align: 'start'
      }
    },

    // Step 4: Add Contact Button
    {
      element: '#sb_contacts',
      popover: {
        title: '➕ Add New Contacts',
        description: `
          Click here to manually add a contact. You'll see a form where you can 
          enter their details—name, email, phone, address, and any custom fields 
          you've created. Great for one-off additions after a meeting or call.
        `,
        side: 'left',
        align: 'start'
      }
    },

    // Step 5: Import Button
    {
      element: '#import-btn',
      popover: {
        title: '📥 Import Contacts in Bulk',
        description: `
          Have a spreadsheet of contacts? Import them all at once using a CSV file. 
          The system will guide you through mapping your columns to contact fields. 
          You can import hundreds or thousands of contacts in minutes.
        `,
        side: 'left',
        align: 'start',
        proTip: 'Download the sample CSV first to see the correct format. This prevents import errors!'
      }
    },

    // Step 6: Filters
    {
      element: 'button:contains("More Filters"), [class*="filter"]',
      popover: {
        title: '🎯 Advanced Filters',
        description: `
          Narrow down your contact list with powerful filters. Filter by tags, 
          date added, location, custom fields, or even whether they've opened 
          your emails. Combine multiple filters to find exactly who you need.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 7: Column Customization
    {
      element: 'button:contains("Columns")',
      popover: {
        title: '📊 Customize Your View',
        description: `
          Choose which columns to display in your contact table. Show only the 
          information that matters to you—hide the rest. This makes scanning 
          your list much faster and more focused.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 8: Bulk Actions
    {
      element: '#tb_bulk-actions',
      popover: {
        title: '⚡ Bulk Actions: Do More in Less Time',
        description: `
          Select multiple contacts (use the checkboxes) and take action on all 
          of them at once. Add tags, send emails, start workflows, export data, 
          or even delete in bulk. Massive time saver for routine tasks.
        `,
        side: 'left',
        align: 'center',
        proTip: 'Use filters first, then "Select All" to act on every filtered contact at once!'
      }
    },

    // Step 9: Contact Table
    {
      element: 'table, [role="table"], [class*="contact-list"]',
      popover: {
        title: '📋 Your Contact List',
        description: `
          This table shows your contacts with key info at a glance. Click any 
          row to open the contact card where you can see their full profile, 
          activity history, notes, tasks, and more. You can sort by any column 
          by clicking the header.
        `,
        side: 'top',
        align: 'center'
      }
    },

    // Step 10: Contact Card (if a contact is selected)
    {
      element: '[class*="contact-card"], [class*="contact-detail"]',
      popover: {
        title: '👤 Contact Card: The Full Picture',
        description: `
          The contact card is where everything about a person lives:
          • Left side: Contact details, tags, notes
          • Right side: Activity timeline, conversations, tasks
          • Bottom: Quick actions (call, text, email)
          
          This is your 360° view of the relationship.
        `,
        side: 'left',
        align: 'start'
      }
    },

    // Step 11: Tags
    {
      element: '[class*="tag"], [class*="label"]',
      popover: {
        title: '🏷️ Tags: Organize Your Way',
        description: `
          Tags are flexible labels you create. Use them for anything: 
          "Hot Lead", "VIP", "Needs Follow-up", "Event Attendee", etc. 
          Tags power Smart Lists, automations, and help you stay organized 
          without rigid categories.
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 12: Custom Fields
    {
      popover: {
        title: '🔧 Custom Fields: Track What Matters',
        description: `
          Beyond standard fields (name, email, phone), you can create custom fields 
          for anything specific to your business: "Budget Range", "Industry", 
          "Preferred Contact Method", "Anniversary Date", etc.
          
          Go to Settings > Custom Fields to create them.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 13: Communication from Contacts
    {
      popover: {
        title: '💬 Communicate Directly',
        description: `
          From any contact card, you can instantly:
          • Call them (if you have a phone number connected)
          • Send an SMS
          • Send an email
          • Start a Facebook/Instagram conversation
          
          All communication is logged automatically in their timeline.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 14: Completion
    {
      popover: {
        title: '🎉 You\'re a Contacts Pro!',
        description: `
          Excellent work! You now know how to:
          ✅ Find any contact instantly
          ✅ Import contacts in bulk
          ✅ Organize with Smart Lists and tags
          ✅ Take bulk actions to save time
          ✅ View complete contact profiles
          
          **Next Steps:**
          • Import your first contacts (or create a few manually)
          • Create a Smart List for your best leads
          • Explore the Conversations tour to learn messaging
          
          Your contact database is now your superpower! 💪
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'Got it! →'
      }
    }
  ]
};
